import type { Rect } from './climb-thumbnail';
import { watermarkSize, type MarkPosition } from './watermark';

/**
 * **Where the stickers — the climb's card, the gym — and the DivePeaks mark
 * sit on a photo or a video**, and how big the stickers are.
 *
 * Kept as fractions of the frame — `x` and `width` of its width, `y` of its
 * height — so what is placed on the preview lands in the same place on the
 * full-size export. A sticker's height is not kept at all: its shape is fixed
 * (the card's by `thumbnailLayout`, the gym's by its name), so it follows from
 * the width; the mark's size is fixed by the frame.
 *
 * The stickers start inside the bounds — on a story or a reel the part of the
 * frame Instagram's own interface leaves uncovered (`safeArea`) — and are
 * then placed by hand anywhere on the frame, whole (user feedback 2026-11-03:
 * the covered parts held them back). **The mark is not placed by hand, nor shown while
 * editing** (user feedback 2026-11-03): only the file made has it, in the
 * first corner of the bounds the stickers leave free (`markPositionFor`), and
 * it is drawn after them, so they never cover it — not even when a
 * celebration grows the card over the frame.
 */
export interface Placement {
  x: number;
  y: number;
  width: number;
}

/** The card's default width, and its smallest, as shares of the frame's short edge. */
const DEFAULT_WIDTH = 0.4;
const MIN_WIDTH = 0.18;
/** Its distance from the frame's edges where it starts, as a share of the short edge. */
const START_MARGIN = 0.035;
/** The air kept between the card and the mark, as a share of the short edge. */
const MARK_GAP = 0.02;

/** The card's box in the frame's pixels. `aspect` is its height over its width. */
export function placementRect(placement: Placement, frameWidth: number, frameHeight: number, aspect: number): Rect {
  const width = placement.width * frameWidth;
  return { x: placement.x * frameWidth, y: placement.y * frameHeight, width, height: width * aspect };
}

function toPlacement(rect: Rect, frameWidth: number, frameHeight: number): Placement {
  return { x: rect.x / frameWidth, y: rect.y / frameHeight, width: rect.width / frameWidth };
}

function grown(rect: Rect, by: number): Rect {
  return { x: rect.x - by, y: rect.y - by, width: rect.width + by * 2, height: rect.height + by * 2 };
}

/** The whole frame, the bounds when nothing covers any of it. */
export function frameBounds(frameWidth: number, frameHeight: number): Rect {
  return { x: 0, y: 0, width: frameWidth, height: frameHeight };
}

function clampInto(rect: Rect, bounds: Rect): Rect {
  return {
    ...rect,
    x: Math.min(bounds.x + bounds.width - rect.width, Math.max(bounds.x, rect.x)),
    y: Math.min(bounds.y + bounds.height - rect.height, Math.max(bounds.y, rect.y)),
  };
}

/** How much of `a` lies over `b`, in square pixels. */
function overlapArea(a: Rect, b: Rect): number {
  const w = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
  const h = Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y);
  return w > 0 && h > 0 ? w * h : 0;
}

/**
 * The card's placement made legal: no smaller than can be read, no bigger
 * than the bounds, and whole inside them.
 */
export function constrainPlacement(
  placement: Placement,
  frameWidth: number,
  frameHeight: number,
  aspect: number,
  bounds: Rect = frameBounds(frameWidth, frameHeight),
): Placement {
  const short = Math.min(frameWidth, frameHeight);
  const largest = Math.min(bounds.width, bounds.height / aspect);
  const width = Math.min(largest, Math.max(MIN_WIDTH * short, placement.width * frameWidth));
  const rect = clampInto(
    { x: placement.x * frameWidth, y: placement.y * frameHeight, width, height: width * aspect },
    bounds,
  );
  return toPlacement(rect, frameWidth, frameHeight);
}

export type Corner = 'bottomLeft' | 'bottomRight' | 'topLeft';

/**
 * Where a sticker starts: in a corner of the bounds — the card bottom-left,
 * leaving the mark's first corner, bottom-right, free — at a share of the
 * frame's short edge for its width.
 */
export function defaultPlacement(
  frameWidth: number,
  frameHeight: number,
  aspect: number,
  bounds: Rect = frameBounds(frameWidth, frameHeight),
  { corner = 'bottomLeft', width: share = DEFAULT_WIDTH }: { corner?: Corner; width?: number } = {},
): Placement {
  const short = Math.min(frameWidth, frameHeight);
  const width = share * short;
  const margin = START_MARGIN * short;
  const x = corner === 'bottomRight' ? bounds.x + bounds.width - margin - width : bounds.x + margin;
  const y = corner === 'topLeft' ? bounds.y + margin : bounds.y + bounds.height - margin - width * aspect;
  return constrainPlacement(
    { x: x / frameWidth, y: y / frameHeight, width: width / frameWidth },
    frameWidth,
    frameHeight,
    aspect,
    bounds,
  );
}

/**
 * Where the mark goes on the file made: the first corner of the bounds —
 * bottom-right, bottom-left, top-right, top-left — that the stickers (with
 * some air round them) leave free; when they leave none, the one they cover
 * least.
 */
export function markPositionFor(
  frameWidth: number,
  frameHeight: number,
  stickers: Rect[],
  bounds: Rect = frameBounds(frameWidth, frameHeight),
): MarkPosition {
  const short = Math.min(frameWidth, frameHeight);
  const size = watermarkSize(frameWidth, frameHeight);
  const margin = START_MARGIN * short;
  const left = bounds.x + margin;
  const right = bounds.x + bounds.width - margin - size;
  const top = bounds.y + margin;
  const bottom = bounds.y + bounds.height - margin - size;
  const corners: Rect[] = [
    { x: right, y: bottom, width: size, height: size },
    { x: left, y: bottom, width: size, height: size },
    { x: right, y: top, width: size, height: size },
    { x: left, y: top, width: size, height: size },
  ];
  const guarded = stickers.map((sticker) => grown(sticker, MARK_GAP * short));
  const covered = (corner: Rect) => guarded.reduce((sum, sticker) => sum + overlapArea(corner, sticker), 0);
  const best = corners.find((corner) => covered(corner) === 0)
    ?? corners.reduce((a, b) => (covered(b) < covered(a) ? b : a));
  return { x: best.x / frameWidth, y: best.y / frameHeight };
}

/** The card moved by a drag of (dx, dy), in the frame's pixels. */
export function movePlacement(
  placement: Placement,
  dx: number,
  dy: number,
  frameWidth: number,
  frameHeight: number,
  aspect: number,
  bounds?: Rect,
): Placement {
  return constrainPlacement(
    { ...placement, x: placement.x + dx / frameWidth, y: placement.y + dy / frameHeight },
    frameWidth,
    frameHeight,
    aspect,
    bounds,
  );
}

/**
 * The card at a new width, in the frame's pixels, grown or shrunk about a
 * point of its own: its centre for a pinch, its bottom-left corner for the
 * handle on the opposite one.
 */
export function resizePlacement(
  placement: Placement,
  newWidth: number,
  anchor: 'centre' | 'bottomLeft',
  frameWidth: number,
  frameHeight: number,
  aspect: number,
  bounds?: Rect,
): Placement {
  const rect = placementRect(placement, frameWidth, frameHeight, aspect);
  const height = newWidth * aspect;
  const x = anchor === 'centre' ? rect.x + (rect.width - newWidth) / 2 : rect.x;
  const y = anchor === 'centre' ? rect.y + (rect.height - height) / 2 : rect.y + rect.height - height;
  return constrainPlacement({ x: x / frameWidth, y: y / frameHeight, width: newWidth / frameWidth }, frameWidth, frameHeight, aspect, bounds);
}
